/**
 * @file useTipDeepLink.js
 * @description 微信内打开打赏深链（?tip=1&tipCode=）时自动拉起打赏弹窗
 * @module features/comic/composables/useTipDeepLink
 */

import { openComicTip } from '@/features/comic/composables/useComicTip'
import {
  consumePendingTipDeepLink,
  savePendingTipDeepLink,
} from '@/features/comic/utils/tipDeepLinkPending'
import { getAuthToken } from '@/shared/utils/auth'

/** @typedef {'opened' | 'need-login'} TipDeepLinkConsumeResult */

/**
 * 从路由解析打赏深链上下文
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @returns {{ context: import('@/features/comic/composables/useComicTip').TipOpenContext, tipCode: string } | null}
 */
export function parseTipDeepLinkFromRoute(route) {
  const tipFlag = route.query.tip
  if (tipFlag !== '1' && tipFlag !== 'true') return null

  const tipCode = String(route.query.tipCode || '').trim()
  if (!tipCode) return null

  const name = route.name
  if (name !== 'ComicDetail' && name !== 'ComicReader') return null

  let comicId = ''
  if (name === 'ComicDetail') {
    comicId = String(route.params.id || '').trim()
  } else {
    comicId = String(route.params.comicId || '').trim()
  }
  if (!comicId) return null

  /** @type {import('@/features/comic/composables/useComicTip').TipOpenContext} */
  const context = {
    comicId,
    source: name === 'ComicReader' ? 'reader' : 'detail',
  }

  if (name === 'ComicReader') {
    const rawVolNum = route.params.volNum
    if (rawVolNum != null && String(rawVolNum).trim() !== '') {
      context.volNum = Number(rawVolNum)
    }
  }

  return { context, tipCode }
}

/**
 * 清除 URL 中的打赏深链 query
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {import('vue-router').Router} router
 */
export function stripTipDeepLinkQuery(route, router) {
  const nextQuery = { ...route.query }
  delete nextQuery.tip
  delete nextQuery.tipCode
  router.replace({ path: route.path, query: nextQuery })
}

/**
 * 从路由 query 消费打赏深链并打开弹窗
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {import('vue-router').Router} router
 * @returns {TipDeepLinkConsumeResult | false} false=非打赏深链
 */
export function consumeTipDeepLinkFromRoute(route, router) {
  const parsed = parseTipDeepLinkFromRoute(route)
  if (!parsed) return false

  const { context, tipCode } = parsed

  if (!getAuthToken()) {
    savePendingTipDeepLink({
      comicId: context.comicId,
      source: context.source,
      volNum: context.volNum,
      tipCode,
    })
    stripTipDeepLinkQuery(route, router)
    return 'need-login'
  }

  const opened = openComicTip(context, { tipCode })
  if (!opened) return false

  stripTipDeepLinkQuery(route, router)
  return 'opened'
}

/**
 * 登录成功后打开暂存的打赏深链
 * @returns {boolean} 是否已打开打赏弹窗
 */
export function openPendingTipDeepLinkAfterLogin() {
  if (!getAuthToken()) return false

  const pending = consumePendingTipDeepLink()
  if (!pending) return false

  return openComicTip(
    {
      comicId: String(pending.comicId),
      source: pending.source,
      volNum: pending.volNum,
    },
    { tipCode: String(pending.tipCode) }
  )
}
